import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
	fetchAnimeDetail,
	fetchAnimePage,
	fetchCountries,
	fetchGenres,
	searchAnimeCatalog,
	sliceSearchCatalog,
} from '@/api/anime';
import { DEFAULT_PAGE_SIZE } from '@/lib/constants';
import type { AnimeListFilters, AnimePage } from '@/types/anime';

export const queryKeys = {
	animeList: (filters: AnimeListFilters) =>
		['anime', 'list', filters] as const,
	animeSearch: (keyword: string) => ['anime', 'search', keyword] as const,
	animeDetail: (slug: string) => ['anime', 'detail', slug] as const,
	genres: ['taxonomy', 'genres'] as const,
	countries: ['taxonomy', 'countries'] as const,
};

/**
 * Một trang danh sách anime. `keepPreviousData` giữ lưới cũ hiển thị khi đổi
 * trang nên UI không nhảy về skeleton mỗi lần bấm phân trang.
 */
export function useAnimeList(filters: AnimeListFilters, enabled = true) {
	return useQuery({
		queryKey: queryKeys.animeList(filters),
		queryFn: ({ signal }) => fetchAnimePage(filters, signal),
		placeholderData: keepPreviousData,
		enabled,
	});
}

export function useAnimeSearch(keyword: string, page = 1) {
	const trimmed = keyword.trim();
	return useQuery({
		queryKey: queryKeys.animeSearch(trimmed),
		queryFn: ({ signal }) => searchAnimeCatalog(trimmed, signal),
		placeholderData: keepPreviousData,
		enabled: trimmed.length > 0,
		select: (catalog): AnimePage =>
			sliceSearchCatalog(catalog, page, DEFAULT_PAGE_SIZE),
	});
}

export function useAnimeDetail(slug: string | undefined) {
	return useQuery({
		queryKey: queryKeys.animeDetail(slug ?? ''),
		queryFn: ({ signal }) => fetchAnimeDetail(slug as string, signal),
		enabled: Boolean(slug),
		// Phim không đổi nội dung thường xuyên; cache lâu hơn danh sách.
		staleTime: 10 * 60 * 1000,
		retry: (failureCount, error) =>
			// 404 thì retry vô nghĩa.
			!(error instanceof Error && 'status' in error && error.status === 404) &&
			failureCount < 2,
	});
}

/** Thể loại và quốc gia gần như tĩnh — cache cả phiên làm việc. */
export function useGenres() {
	return useQuery({
		queryKey: queryKeys.genres,
		queryFn: ({ signal }) => fetchGenres(signal),
		staleTime: Infinity,
	});
}

export function useCountries() {
	return useQuery({
		queryKey: queryKeys.countries,
		queryFn: ({ signal }) => fetchCountries(signal),
		staleTime: Infinity,
	});
}
